import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    DateField,
    NumberField,
    FunctionField,
    EditButton,
    ShowButton,
    Filter,
    TextInput,
    BooleanInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    usePermissions,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Typography, useMediaQuery, useTheme, Fab } from '@mui/material';
import { CheckCircle, Cancel, LocationOn, ChevronRight, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getLocalizedAddressData } from '../../data/hongKongAddressData';

// Filter component for sites
const SitesFilter = (props: any) => {
    const translate = useTranslate();
    
    return (
        <Filter {...props}>
            <TextInput 
                label={translate('resources.sites.list.filters.search')} 
                source="q" 
                alwaysOn 
            />
            <BooleanInput 
                label={translate('resources.sites.list.filters.activeOnly')} 
                source="isActive" 
            />
            <SelectInput
                source="accessType"
                label={translate('resources.sites.fields.accessType')}
                choices={[
                    { id: 'public', name: translate('resources.sites.accessTypes.public') },
                    { id: 'private', name: translate('resources.sites.accessTypes.private') },
                    { id: 'restricted', name: translate('resources.sites.accessTypes.restricted') },
                ]}
            />
            <TextInput 
                label={translate('resources.sites.fields.city')} 
                source="city" 
            />
            <TextInput 
                label={translate('resources.sites.fields.state')} 
                source="state" 
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

// Helper function to get state display name
const getStateDisplayName = (stateKey: string, language: string = 'en') => {
    const addressData = getLocalizedAddressData(language);

    // Find the district that matches this key
    for (const region of addressData) {
        const district = region.districts.find(d => d.key === stateKey);
        if (district) {
            return district.name;
        }
    }

    // Fallback to the original value if not found
    return stateKey;
};

const MobileSitesList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();

    if (isLoading) return null;
    if (!data || data.length === 0) return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" sx={{ color: '#606060' }}>
                {translate('ra.navigation.no_results')}
            </Typography>
        </Box>
    );

    const currentLocale = translate('language.current', { _: 'en' });
    const language = currentLocale === 'zh-TW' ? 'zh-TW' : 'en';

    return (
        <Box>
            {data.map((record: any) => {
                const displayName = record.state ? getStateDisplayName(record.state, language) : record.city;
                const accessColor = record.accessType === 'public' ? '#009A75' :
                                    record.accessType === 'private' ? '#EF6C00' : '#7B1FA2';
                const accessBg = record.accessType === 'public' ? '#E8F5E8' :
                                 record.accessType === 'private' ? '#FFF3E0' : '#F3E5F5';

                return (
                    <RecordContextProvider key={record.id} value={record}>
                        <Box
                            onClick={() => navigate(`/sites/${record.id}`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                py: 1.5,
                                px: 2,
                                borderBottom: '1px solid #F0F0F0',
                                cursor: 'pointer',
                                '&:active': { backgroundColor: '#F5F5F5' },
                            }}
                        >
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: record.isActive ? '#009A75' : '#F44336',
                                flexShrink: 0,
                            }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                    }}>
                                        {record.name}
                                    </Typography>
                                    <Chip
                                        label={translate(`resources.sites.accessTypes.${record.accessType}`)}
                                        size="small"
                                        component="div"
                                        clickable={false}
                                        sx={{
                                            backgroundColor: accessBg,
                                            color: accessColor,
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            height: 22,
                                            flexShrink: 0,
                                            '&:hover': { backgroundColor: accessBg },
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn sx={{ fontSize: 14, color: '#909090' }} />
                                    <Typography sx={{ color: '#606060', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {displayName}
                                    </Typography>
                                    {record.vendorName && (
                                        <Typography sx={{ color: '#909090', fontSize: '0.75rem', ml: 1 }}>
                                            {record.vendorName}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                    <Typography sx={{ color: '#606060', fontSize: '0.7rem' }}>
                                        {record.chargerCount ?? 0} {translate('resources.sites.fields.chargers', { _: 'chargers' })}
                                    </Typography>
                                    {record.reservationEnabled && (
                                        <Typography sx={{ color: '#009A75', fontSize: '0.7rem', fontWeight: 500 }}>
                                            {translate('resources.sites.fields.reservationEnabled', { _: 'Reservation' })}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <ChevronRight sx={{ color: '#BDBDBD', fontSize: 20, flexShrink: 0 }} />
                        </Box>
                    </RecordContextProvider>
                );
            })}
        </Box>
    );
};

export const SitesList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const canEdit = permissions?.includes('sites:write:all');

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<SitesFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': {
                            margin: 0,
                        },
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': {
                            display: 'none',
                        },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiInputBase-root': {
                            width: '100% !important',
                        },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileSitesList />
                ) : (
                    <Datagrid
                    bulkActionButtons={false}
                    rowClick="show"
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                        },
                        '& .RaDatagrid-row': {
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#F0F7FF',
                            },
                        },
                    }}
                >
                    <TextField
                        source="name"
                        label={translate('resources.sites.fields.premisesName')}
                    />

                    <TextField
                        source="vendorName"
                        label={translate('resources.sites.fields.merchant')}
                        emptyText="-"
                    />

                    <FunctionField
                        source="location"
                        label={translate('resources.sites.fields.district')}
                        render={(record: any) => {
                            const currentLocale = translate('language.current', { _: 'en' });
                            const language = currentLocale === 'zh-TW' ? 'zh-TW' : 'en';
                            const displayName = record.state ? getStateDisplayName(record.state, language) : record.city;

                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                                    <span style={{ fontSize: '0.9em' }}>
                                        {displayName}
                                    </span>
                                </Box>
                            );
                        }}
                    />

                    <FunctionField
                        source="accessType"
                        render={(record: any) => (
                            <span style={{ pointerEvents: 'none' }}>
                                <Chip
                                    label={translate(`resources.sites.accessTypes.${record.accessType}`)}
                                    size="small"
                                    component="div"
                                    clickable={false}
                                    sx={{
                                        backgroundColor: record.accessType === 'public' ? '#E8F5E8' :
                                                        record.accessType === 'private' ? '#FFF3E0' : '#F3E5F5',
                                        color: record.accessType === 'public' ? '#009A75' :
                                               record.accessType === 'private' ? '#EF6C00' : '#7B1FA2',
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: record.accessType === 'public' ? '#E8F5E8' :
                                                            record.accessType === 'private' ? '#FFF3E0' : '#F3E5F5',
                                        },
                                    }}
                                />
                            </span>
                        )}
                    />

                    <NumberField source="chargerCount" />

                    <BooleanField
                        source="isActive"
                        valueLabelTrue={translate('status.active')}
                        valueLabelFalse={translate('status.inactive')}
                        TrueIcon={CheckCircle}
                        FalseIcon={Cancel}
                        sx={{
                            '& .RaBooleanField-true': {
                                color: '#009A75',
                            },
                            '& .RaBooleanField-false': {
                                color: '#F44336',
                            },
                        }}
                    />

                    <BooleanField
                        source="reservationEnabled"
                        TrueIcon={CheckCircle}
                        FalseIcon={Cancel}
                        sx={{
                            '& .RaBooleanField-true': {
                                color: '#009A75',
                            },
                            '& .RaBooleanField-false': {
                                color: '#909090',
                            },
                        }}
                    />

                    <DateField
                        source="createdAt"
                        options={{
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        }}
                    />

                    {canEdit ? <EditButton /> : <ShowButton />}
                </Datagrid>
            )}
        </List>
        {isMobile && (
            <Fab
                color="primary"
                onClick={() => navigate('/sites/create')}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    backgroundColor: '#009A75',
                    '&:hover': { backgroundColor: '#007A5E' },
                    zIndex: 1100,
                }}
            >
                <AddIcon />
            </Fab>
        )}
        </Box>
    );
};